import { useEffect } from 'react';

export interface OgFields {
  title?: string;
  description?: string;
  image?: string;
  url?: string;
  type?: string;
}

export interface DocumentHeadOptions {
  title?: string;
  description?: string;
  og?: OgFields;
  schemaOrg?: Record<string, unknown> | Array<Record<string, unknown>>;
  canonical?: string;
}

function setMeta(name: string, content: string, attr: 'name' | 'property' = 'name'): () => void {
  if (typeof document === 'undefined') return () => undefined;
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${name}"]`);
  const prev = el ? el.getAttribute('content') : null;
  const created = !el;
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
  return () => {
    if (!el) return;
    if (created) {
      el.remove();
      return;
    }
    if (prev === null) el.removeAttribute('content');
    else el.setAttribute('content', prev);
  };
}

function setLink(rel: string, href: string): () => void {
  if (typeof document === 'undefined') return () => undefined;
  let el = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  const prev = el ? el.getAttribute('href') : null;
  const created = !el;
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
  return () => {
    if (!el) return;
    if (created) {
      el.remove();
      return;
    }
    if (prev === null) el.removeAttribute('href');
    else el.setAttribute('href', prev);
  };
}

function injectSchema(data: unknown): () => void {
  if (typeof document === 'undefined') return () => undefined;
  const script = document.createElement('script');
  script.type = 'application/ld+json';
  script.setAttribute('data-managed', 'use-document-head');
  script.textContent = JSON.stringify(data);
  document.head.appendChild(script);
  return () => script.remove();
}

export default function useDocumentHead(opts: DocumentHeadOptions): void {
  useEffect(() => {
    const cleanups: Array<() => void> = [];
    const prevTitle = document.title;
    if (opts.title) document.title = opts.title;
    cleanups.push(() => {
      document.title = prevTitle;
    });
    if (opts.description) cleanups.push(setMeta('description', opts.description));
    if (opts.canonical) cleanups.push(setLink('canonical', opts.canonical));
    if (opts.og) {
      if (opts.og.title) cleanups.push(setMeta('og:title', opts.og.title, 'property'));
      if (opts.og.description) cleanups.push(setMeta('og:description', opts.og.description, 'property'));
      if (opts.og.image) cleanups.push(setMeta('og:image', opts.og.image, 'property'));
      if (opts.og.url) cleanups.push(setMeta('og:url', opts.og.url, 'property'));
      if (opts.og.type) cleanups.push(setMeta('og:type', opts.og.type, 'property'));
    }
    if (opts.schemaOrg) cleanups.push(injectSchema(opts.schemaOrg));
    return () => {
      for (let i = cleanups.length - 1; i >= 0; i -= 1) cleanups[i]();
    };
  }, [opts]);
}
